import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { COUNTRY_CODES, PHONE, PHONE_TEL } from "../data/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    dialCode: "+30",
    phone: "",
    date: "",
    time: "19:00",
    guests: 2,
    message: "",
};

const ReserveModal = ({ open, onClose }) => {
    const { t } = useLang();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    if (!open) return null;

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: name === "guests" ? Number(value) : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                reservation_type: "table",
                name: form.name,
                email: form.email,
                phone: `${form.dialCode} ${form.phone}`.trim(),
                date: form.date,
                time: form.time,
                guests: form.guests,
                message: form.message || "",
            };
            await axios.post(`${API}/reservations`, payload);
            toast.success(t("reserve.success"));
            setForm(initial);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(t("reserve.error"));
        } finally {
            setLoading(false);
        }
    };

    const fieldCls =
        "w-full px-4 py-3 bg-white border border-aegean-200 rounded-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-aegean-400 focus:ring-2 focus:ring-aegean-300/40 transition";
    const labelCls =
        "block font-display uppercase tracking-[0.22em] text-xs text-aegean-700 mb-2";

    return (
        <div
            data-testid="reserve-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-6 bg-aegean-900/70 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-ivory shadow-2xl border border-aegean-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-aegean-400 text-white px-6 md:px-10 py-5 flex items-center justify-between">
                    <div>
                        <p className="font-display uppercase tracking-[0.32em] text-[10px] text-white/80">
                            {t("nav.reserveTable")}
                        </p>
                        <h2 className="font-accent italic text-2xl md:text-3xl leading-tight mt-1">
                            {t("reserve.title")}
                        </h2>
                    </div>
                    <button
                        data-testid="reserve-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                        className="w-10 h-10 grid place-items-center rounded-full hover:bg-white/20 transition"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="px-6 md:px-10 py-7">
                    <p className="text-ink/70 mb-7 text-sm">{t("reserve.subtitle")}</p>

                    <form onSubmit={onSubmit} data-testid="reserve-form">
                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className={labelCls} htmlFor="r-name">{t("reserve.name")} *</label>
                                <input id="r-name" name="name" data-testid="reserve-name" required value={form.name} onChange={onChange} className={fieldCls} />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="r-email">{t("reserve.email")} *</label>
                                <input id="r-email" name="email" type="email" data-testid="reserve-email" required value={form.email} onChange={onChange} className={fieldCls} />
                            </div>

                            <div className="md:col-span-2">
                                <label className={labelCls} htmlFor="r-phone">{t("reserve.phone")} *</label>
                                <div className="flex gap-2">
                                    <div className="relative shrink-0">
                                        <select
                                            name="dialCode"
                                            data-testid="reserve-dial-code"
                                            value={form.dialCode}
                                            onChange={onChange}
                                            className={`${fieldCls} appearance-none pr-9 cursor-pointer min-w-[130px]`}
                                            aria-label="Country code"
                                        >
                                            {COUNTRY_CODES.map((c) => (
                                                <option key={`${c.iso}-${c.code}`} value={c.code}>
                                                    {c.iso} {c.code}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-aegean-500" />
                                    </div>
                                    <input
                                        id="r-phone"
                                        name="phone"
                                        type="tel"
                                        inputMode="tel"
                                        data-testid="reserve-phone"
                                        required
                                        placeholder="697 414 1414"
                                        value={form.phone}
                                        onChange={onChange}
                                        className={`${fieldCls} flex-1`}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelCls} htmlFor="r-date">{t("reserve.date")} *</label>
                                <input id="r-date" name="date" type="date" data-testid="reserve-date" required value={form.date} onChange={onChange} className={fieldCls} />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="r-time">{t("reserve.time")} *</label>
                                <input id="r-time" name="time" type="time" data-testid="reserve-time" required value={form.time} onChange={onChange} className={fieldCls} />
                            </div>

                            <div className="md:col-span-2">
                                <label className={labelCls} htmlFor="r-guests">{t("reserve.guests")} *</label>
                                <input
                                    id="r-guests"
                                    name="guests"
                                    type="number"
                                    min="1"
                                    max="30"
                                    data-testid="reserve-guests"
                                    required
                                    value={form.guests}
                                    onChange={onChange}
                                    className={fieldCls}
                                />
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className={labelCls} htmlFor="r-message">{t("reserve.message")}</label>
                            <textarea
                                id="r-message"
                                name="message"
                                data-testid="reserve-message"
                                value={form.message}
                                onChange={onChange}
                                rows={3}
                                className={fieldCls}
                            />
                        </div>

                        <div className="mt-7 flex flex-col md:flex-row md:items-center gap-4">
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="reserve-submit"
                                className="btn-primary w-full md:w-auto"
                            >
                                {loading ? t("reserve.sending") : t("reserve.submit")} <ArrowRight size={16} />
                            </button>
                            <div className="text-sm text-ink/60">
                                {t("reserve.or")}{" "}
                                <a href={`tel:${PHONE_TEL}`} className="text-aegean-600 hover:text-aegean-500 font-medium">
                                    {PHONE}
                                </a>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReserveModal;
